import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { NavBar } from '../components/NavBar';
import { toast } from '../stores/toast.store';

type Platform = 'instagram' | 'tiktok' | 'youtube' | 'linkedin' | 'telegram';

interface CalendarItem {
  id: string;
  platform: Platform;
  status: 'queued' | 'publishing' | 'published' | 'failed' | 'cancelled';
  topicTitle: string;
  scheduledAt: string;
  postUrl: string | null;
}

const PLATFORM_COLOR: Record<Platform, string> = {
  instagram: 'bg-gradient-to-br from-pink-500 to-rose-500',
  tiktok:    'bg-gradient-to-br from-slate-700 to-slate-900',
  youtube:   'bg-gradient-to-br from-red-500 to-red-700',
  linkedin:  'bg-gradient-to-br from-blue-600 to-blue-800',
  telegram:  'bg-gradient-to-br from-sky-400 to-sky-600',
};

const STATUS_BADGE: Record<CalendarItem['status'], string> = {
  queued:     'badge-amber',
  publishing: 'badge-indigo',
  published:  'badge-green',
  failed:     'badge-red',
  cancelled:  'badge-slate',
};

function monthName(m: number): string {
  return ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'][m - 1]!;
}

export default function Calendar() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData]   = useState<Record<string, CalendarItem[]>>({});
  const [selected, setSelected] = useState<string | null>(null);

  const load = () =>
    api.get(`/publications/calendar?year=${year}&month=${month}`)
      .then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, year, month]);

  const days = useMemo(() => {
    const firstDay = new Date(Date.UTC(year, month - 1, 1));
    const startWeekday = (firstDay.getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const cells: Array<{ key: string; day: number | null }> = [];
    for (let i = 0; i < startWeekday; i++) cells.push({ key: `empty-${i}`, day: null });
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ key, day: d });
    }
    return cells;
  }, [year, month]);

  const prev = () => { if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1); };

  async function cancel(id: string) {
    if (!confirm('Отменить публикацию?')) return;
    await api.delete(`/publications/${id}`);
    toast.success('Отменено'); load();
  }
  async function reschedule(id: string) {
    const newDate = prompt('Новая дата/время (YYYY-MM-DDTHH:MM:SSZ):');
    if (!newDate) return;
    try { await api.patch(`/publications/${id}`, { scheduledAt: newDate }); load(); }
    catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Ошибка');
    }
  }

  const selectedItems = selected ? data[selected] ?? [] : [];
  const todayKey = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6 pb-16">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Календарь публикаций</h1>
          <p className="text-slate-500 mt-1.5">Когда и куда уйдут твои видео. Цветные точки = платформы.</p>
        </header>

        <div className="card-glass p-3 mb-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-1.5">
            <button onClick={prev} className="btn btn-sm btn-ghost">‹</button>
            <div className="px-4 py-2 font-semibold text-sm">{monthName(month)} {year}</div>
            <button onClick={next} className="btn btn-sm btn-ghost">›</button>
          </div>
          <div className="flex gap-3 text-xs text-slate-500 flex-wrap">
            {(Object.keys(PLATFORM_COLOR) as Platform[]).map((p) => (
              <div key={p} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${PLATFORM_COLOR[p]}`} />
                <span>{p}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-glass overflow-hidden">
          <div className="grid grid-cols-7 bg-slate-50/60 text-xs font-semibold text-slate-500">
            {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map((d) => (
              <div key={d} className="p-2.5 text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((cell) => {
              if (cell.day === null) return <div key={cell.key} className="h-24 border-t border-slate-100/80 bg-slate-50/30" />;
              const items = data[cell.key] ?? [];
              const isSelected = selected === cell.key;
              const isToday = cell.key === todayKey;
              return (
                <button
                  key={cell.key}
                  onClick={() => setSelected(cell.key === selected ? null : cell.key)}
                  className={`h-24 border-t border-l border-slate-100/80 p-2 text-left hover:bg-indigo-50/40 transition ${
                    isSelected ? 'bg-indigo-50/70' : ''
                  }`}
                >
                  <div className={isToday
                    ? 'w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-semibold flex items-center justify-center text-xs'
                    : 'text-xs text-slate-500'
                  }>
                    {cell.day}
                  </div>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {items.slice(0, 6).map((it) => (
                      <span key={it.id} title={`${it.platform} · ${it.topicTitle}`}
                            className={`w-2 h-2 rounded-full ${PLATFORM_COLOR[it.platform]}`} />
                    ))}
                    {items.length > 6 && <span className="text-[10px] text-slate-500">+{items.length - 6}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {selected && (
          <div className="card-glass mt-4 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">📅 {selected}</h3>
              <button onClick={() => setSelected(null)} className="text-sm text-slate-500 hover:text-slate-900">закрыть</button>
            </div>
            {selectedItems.length === 0 ? (
              <p className="text-sm text-slate-500">Нет публикаций на этот день.</p>
            ) : (
              <ul className="space-y-2">
                {selectedItems.map((it) => (
                  <li key={it.id} className="flex items-center gap-3 text-sm py-2.5 border-b last:border-0 border-slate-100">
                    <span className={`w-2.5 h-2.5 rounded-full ${PLATFORM_COLOR[it.platform]}`} />
                    <span className="font-medium w-20 capitalize">{it.platform}</span>
                    <span className="flex-1 truncate">{it.topicTitle}</span>
                    <span className={`badge ${STATUS_BADGE[it.status]}`}>{it.status}</span>
                    <span className="text-xs text-slate-500">
                      {new Date(it.scheduledAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {it.postUrl && (
                      <a href={it.postUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline">ссылка →</a>
                    )}
                    {it.status === 'queued' && (
                      <>
                        <button onClick={() => reschedule(it.id)} className="btn btn-sm btn-ghost">✎</button>
                        <button onClick={() => cancel(it.id)} className="btn btn-sm btn-ghost text-rose-600">✕</button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
